/**
 * The settings screen edits a flat form model: `CmsSettings` keys contain dots
 * (`drm.enforce`), which react-hook-form would read as nested paths. Kept apart from the
 * component so both directions are unit-testable.
 */
import type { CmsSettings, CmsSettingsPutBody } from '@akpl/shared';
import { z } from 'zod';

export const SettingsFormSchema = z.object({
  supportEmail: z.string().trim().email(),
  controlsTimeoutMs: z.coerce.number().int().min(1000).max(600_000),
  sessionHours: z.coerce.number().int().min(1).max(72),
  maintenanceEnabled: z.boolean(),
  maintenanceEn: z.string().max(500),
  maintenanceEs: z.string().max(500),
  entitlementsEnforce: z.boolean(),
  drmEnforce: z.boolean(),
  billingCheckoutEnabled: z.boolean(),
  authSocialEnabled: z.boolean(),
});
export type SettingsForm = z.infer<typeof SettingsFormSchema>;

export function toForm(settings: CmsSettings): SettingsForm {
  return {
    supportEmail: settings.support_email,
    controlsTimeoutMs: settings.controls_timeout_ms,
    sessionHours: settings.session_hours,
    maintenanceEnabled: settings.maintenance_banner.enabled,
    maintenanceEn: settings.maintenance_banner.text.en,
    maintenanceEs: settings.maintenance_banner.text.es,
    entitlementsEnforce: settings['entitlements.enforce'],
    drmEnforce: settings['drm.enforce'],
    billingCheckoutEnabled: settings['billing.checkout_enabled'],
    authSocialEnabled: settings['auth.social_enabled'],
  };
}

export function fromForm(form: SettingsForm): CmsSettings {
  return {
    support_email: form.supportEmail,
    controls_timeout_ms: form.controlsTimeoutMs,
    session_hours: form.sessionHours,
    maintenance_banner: {
      enabled: form.maintenanceEnabled,
      text: { en: form.maintenanceEn, es: form.maintenanceEs },
    },
    'entitlements.enforce': form.entitlementsEnforce,
    'drm.enforce': form.drmEnforce,
    'billing.checkout_enabled': form.billingCheckoutEnabled,
    'auth.social_enabled': form.authSocialEnabled,
  };
}

const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Only the keys the operator actually changed reach `PUT /cms/settings` (and the audit row). */
export function changedKeys(current: CmsSettings, next: CmsSettings): CmsSettingsPutBody {
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(next) as (keyof CmsSettings)[]) {
    if (!eq(current[key], next[key])) patch[key] = next[key];
  }
  return patch as CmsSettingsPutBody;
}
